'use client';

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { settingsService } from '@/services/settingsService';
import { getThemeValues, type ThemeMode, type ThemePalette } from './corporateTheme';

interface AdminThemeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => Promise<void>;
  NP: ThemePalette;
  npFilterClass: (isActive: boolean) => string;
  npFilterBadge: (isActive: boolean) => string;
  npStatusBadge: (status: string) => string;
}

// ─── CSS Custom Properties mapping ──────────────────────────────────────────
const THEME_CSS_VARS: Record<string, { dark: string; light: string }> = {
  '--admin-text':          { dark: '#F4F6F8',                    light: '#0F172A' },
  '--admin-muted':         { dark: '#9BA7B4',                    light: '#64748B' },
  '--admin-subtle':        { dark: '#6B7A8A',                    light: '#94A3B8' },
  '--admin-secondary':     { dark: '#C5CDD8',                    light: '#475569' },
  // Accent uniforme blu marino in dark e light (rimossi i toni sabbia/oro)
  '--admin-accent':        { dark: '#60A5FA',                    light: '#3B82F6' },
  '--admin-accent-soft':   { dark: '#93C5FD',                    light: '#60A5FA' },
  '--admin-accent-glass':  { dark: 'rgba(96,165,250,0.10)',      light: 'rgba(59,130,246,0.08)' },
  '--admin-accent-glass-2':{ dark: 'rgba(96,165,250,0.18)',      light: 'rgba(59,130,246,0.12)' },
  '--admin-accent-border': { dark: 'rgba(96,165,250,0.25)',      light: 'rgba(59,130,246,0.25)' },
  '--admin-accent-border-2':{ dark: 'rgba(96,165,250,0.35)',      light: 'rgba(59,130,246,0.35)' },
  '--admin-glass-1':       { dark: 'rgba(255,255,255,0.04)',     light: 'rgba(15,23,42,0.03)' },
  '--admin-glass-2':       { dark: 'rgba(255,255,255,0.06)',     light: 'rgba(15,23,42,0.05)' },
  '--admin-glass-3':       { dark: 'rgba(255,255,255,0.1)',      light: 'rgba(15,23,42,0.07)' },
  '--admin-border':        { dark: 'rgba(255,255,255,0.08)',     light: '#E2E8F0' },
  '--admin-border-2':      { dark: 'rgba(255,255,255,0.1)',      light: '#CBD5E1' },
  '--admin-border-3':      { dark: 'rgba(255,255,255,0.12)',     light: '#94A3B8' },
  '--admin-border-4':      { dark: 'rgba(255,255,255,0.2)',      light: '#94A3B8' },
  '--admin-info':          { dark: '#4DA6FF',                    light: '#2563EB' },
  '--admin-bg-deep':       { dark: '#0D1B30',                    light: '#F8FAFC' },
  '--admin-bg-card':       { dark: '#1B3254',                    light: '#FFFFFF' },
  '--admin-input-bg':      { dark: 'rgba(255,255,255,0.06)',     light: '#FFFFFF' },
  '--admin-input-border':  { dark: 'rgba(255,255,255,0.1)',      light: '#CBD5E1' },
  // btn-text deve essere chiaro su accent blu (in dark prima era scuro su oro)
  '--admin-btn-text':      { dark: '#FFFFFF',                    light: '#FFFFFF' },
};

function injectCssVars(mode: ThemeMode) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  for (const [prop, values] of Object.entries(THEME_CSS_VARS)) {
    root.style.setProperty(prop, values[mode]);
  }
}

const STORAGE_KEY = 'insulacharter_theme_mode';

function getInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'dark';
  const saved = localStorage.getItem(STORAGE_KEY);
  return saved === 'light' ? 'light' : 'dark';
}

const defaultValues = getThemeValues('dark');

const AdminThemeContext = createContext<AdminThemeContextValue>({
  mode: 'dark',
  setMode: async () => {},
  ...defaultValues,
});

export function AdminThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);
  const [theme, setTheme] = useState(() => getThemeValues(getInitialMode()));

  // Inject CSS vars on mount and mode change
  useEffect(() => {
    injectCssVars(mode);
  }, [mode]);

  // Sync from DB on mount (fallback, localStorage is primary).
  // Skip se non siamo in area admin/vendor/excursion: l'endpoint /api/settings
  // è admin-only e su pagine pubbliche genera 401 inutile in console.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = window.location.pathname;
    const isPrivilegedArea =
      path.startsWith('/admin') || path.startsWith('/partner');
    if (!isPrivilegedArea) return;

    settingsService.getAllSettings()
      .then(settings => {
        const themeSetting = settings.find(s => s.key === 'admin_theme');
        if (themeSetting?.value?.mode) {
          const saved = themeSetting.value.mode as ThemeMode;
          if (!localStorage.getItem(STORAGE_KEY)) {
            setModeState(saved);
            setTheme(getThemeValues(saved));
            localStorage.setItem(STORAGE_KEY, saved);
          }
        }
      })
      .catch(() => {});
  }, []);

  const setMode = useCallback(async (newMode: ThemeMode) => {
    setModeState(newMode);
    setTheme(getThemeValues(newMode));
    localStorage.setItem(STORAGE_KEY, newMode);
    // Save to DB in background (best-effort)
    settingsService.updateSetting('admin_theme', { mode: newMode }).catch(() => {});
  }, []);

  return (
    <AdminThemeContext.Provider value={{ mode, setMode, ...theme }}>
      {children}
    </AdminThemeContext.Provider>
  );
}

export function useAdminTheme() {
  return useContext(AdminThemeContext);
}
